import { useCallback, useMemo, useState } from 'react';

import type { GetLeadCardResponse } from '@app/api/Client/types';
import {
  useMarkLeadsAsAnsweredMutation,
  useMarkLeadsAsSpamMutation,
  useTakeLeadMutation,
} from '@app/api/Leads/api';
import type {
  LeadCardActionButtonItemData,
  TaskPreviewItemData,
  LeadCardTaskStatus,
} from '@app/components/LeadCard/data';
import {
  CANCEL,
  FRONT_WARNING,
  RIGHTS_IS_ALLOW_SPAM,
  RIGHTS_LEADS,
  SUCCESS,
  WARNING,
} from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { useLeadAnswerTimer } from './useLeadAnswerTimer';
import { useTaskActions } from './useTaskActions';

import type { BaseState } from './useBaseState';

type LeadCardFooterActionId =
  | 'accept-dialog'
  | 'disable-answers'
  | 'return-from-spam';

interface LeadCardFooterConfirmationState {
  isOpen: boolean;
  title: string;
  text: string;
  okText: string;
  cancelText: string;
  isDanger: boolean;
  isLoading: boolean;
  onCancel(): void;
  onConfirm(): void;
}

interface LeadCardAnswerTimerState {
  isVisible: boolean;
  text: string;
}

interface UseLeadCardFooterResult {
  footerActions: LeadCardActionButtonItemData[];
  footerTasks: TaskPreviewItemData[];
  footerConfirmation: LeadCardFooterConfirmationState;
  answerTimer: LeadCardAnswerTimerState;
}

type UseLeadCardFooterParams = Pick<
  BaseState,
  | 'applyLeadUpdate'
  | 'leadCardData'
  | 'refetchLeadCard'
  | 'refreshLatestHistory'
>;

type LeadCardTask = NonNullable<
  GetLeadCardResponse['ClientView']['ClientCrmTasks']
>[number];
type LeadCardTaskOwner = NonNullable<LeadCardTask['Owners']>[number];

const ACCEPT_DIALOG_ACTION: LeadCardFooterActionId = 'accept-dialog';
const DISABLE_ANSWERS_ACTION: LeadCardFooterActionId = 'disable-answers';
const RETURN_FROM_SPAM_ACTION: LeadCardFooterActionId = 'return-from-spam';

const TASK_STATUS_IN_PROGRESS = 1;
const TASK_STATUS_COMPLETED = 2;
const TASK_STATUS_OVERDUE = 3;
const TASK_STATUS_COMPLETED_OVERDUE = 4;

const ACTION_TEXTS: Record<
  LeadCardFooterActionId,
  { full: string; short: string; success: string }
> = {
  [ACCEPT_DIALOG_ACTION]: {
    short: 'Закрепить себя',
    full: 'закрепить себя за лидом',
    success: 'Диалог закреплен успешно',
  },
  [DISABLE_ANSWERS_ACTION]: {
    short: 'Подтвердить',
    full: 'отметить сообщение как отвеченное',
    success: 'Сообщение отмечено отвеченным',
  },
  [RETURN_FROM_SPAM_ACTION]: {
    short: 'Вернуть из спама',
    full: 'вернуть лид из спама',
    success: 'Лид возвращен из спама',
  },
};

const getOwnerName = ({
  NameFirst,
  NameLast,
  NameMiddle,
}: LeadCardTaskOwner): string =>
  [NameLast, NameFirst, NameMiddle].filter(Boolean).join(' ');

const getTaskStatus = ({
  IsCompleted,
  Status,
}: Pick<LeadCardTask, 'IsCompleted' | 'Status'>): LeadCardTaskStatus => {
  if (
    IsCompleted ||
    Status === TASK_STATUS_COMPLETED ||
    Status === TASK_STATUS_COMPLETED_OVERDUE
  ) {
    return 'completed';
  }

  if (Status === TASK_STATUS_OVERDUE) {
    return 'overdue';
  }

  if (Status === TASK_STATUS_IN_PROGRESS) {
    return 'active';
  }

  return 'pending';
};

const getTasks = (
  leadCardData: GetLeadCardResponse,
  openTask: (task: LeadCardTask) => void
): TaskPreviewItemData[] =>
  (leadCardData.ClientView.ClientCrmTasks ?? []).map((task) => ({
    id: task.Id,
    taskTypeName: task.TaskType?.trim() || 'Без типа',
    status: getTaskStatus(task),
    startsAt: task.TimeStart,
    endsAt: task.TimeDeadline,
    assigneeName: (task.Owners ?? [])
      .map(getOwnerName)
      .filter(Boolean)
      .join(', '),
    branchName: task.Branch ?? '',
    description: task.Description,
    taskTypeIconId: task.TaskTypeIconId ?? null,
    taskTypeColor: task.TaskTypeColor ?? null,
    onOpen: () => openTask(task),
  }));

export const useFooter = ({
  applyLeadUpdate,
  leadCardData,
  refetchLeadCard,
  refreshLatestHistory,
}: UseLeadCardFooterParams): UseLeadCardFooterResult => {
  const {
    params: { ActiveBranchId, UserId },
  } = useUserPrint();
  const { userAccessRights } = useNewUserAccessRights();
  const { showNotification } = useNotification();
  const { openPreviewTaskModal } = useTaskActions();

  const [confirmationActionId, setConfirmationActionId] =
    useState<LeadCardFooterActionId | null>(null);
  const [pendingActionId, setPendingActionId] =
    useState<LeadCardFooterActionId | null>(null);

  const [takeLead] = useTakeLeadMutation();
  const [markLeadsAsAnswered] = useMarkLeadsAsAnsweredMutation();
  const [markLeadsAsSpam] = useMarkLeadsAsSpamMutation();

  const canViewTasks = userAccessRights?.CrmTasks?.IsOn === true;
  const canManageSpam =
    userAccessRights?.[RIGHTS_LEADS]?.[RIGHTS_IS_ALLOW_SPAM] === true;

  const { shouldShowAnswerTimer, countdownLabel } = useLeadAnswerTimer({
    isSpam: Boolean(leadCardData?.Lead.SpamTime),
    isNoAnswer: Boolean(leadCardData?.Lead.IsNoAnswer),
    userAnswerTill: leadCardData?.Lead.UserAnswerTill ?? null,
    serverTimeUtc: leadCardData?.ServerTimeUtc ?? null,
  });

  const openTask = useCallback(
    (task: LeadCardTask) => {
      openPreviewTaskModal(task);
    },
    [openPreviewTaskModal]
  );

  const openConfirmation = useCallback((actionId: LeadCardFooterActionId) => {
    setConfirmationActionId(actionId);
  }, []);

  const closeConfirmation = useCallback(() => {
    if (pendingActionId !== null) return;

    setConfirmationActionId(null);
  }, [pendingActionId]);

  const runAction = useCallback(
    async (actionId: LeadCardFooterActionId) => {
      if (pendingActionId !== null) return;

      const leadId = leadCardData?.LeadId ?? null;
      if (leadId === null) {
        showNotification('Лид не был распознан', FRONT_WARNING);
        return;
      }

      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', FRONT_WARNING);
        return;
      }

      try {
        setPendingActionId(actionId);
        let shouldRefetchLeadCard = true;

        switch (actionId) {
          case ACCEPT_DIALOG_ACTION:
            await takeLead({ ActiveBranchId, leadId }).unwrap();
            break;
          case DISABLE_ANSWERS_ACTION: {
            const updatedLeads = await markLeadsAsAnswered({
              ActiveBranchId,
              data: [leadId],
            }).unwrap();
            const updatedLead =
              updatedLeads.find((lead) => lead.Id === leadId) ?? null;
            if (updatedLead) {
              applyLeadUpdate(updatedLead);
              shouldRefetchLeadCard = false;
            }
            break;
          }
          case RETURN_FROM_SPAM_ACTION:
            await markLeadsAsSpam({
              ActiveBranchId,
              isSpam: false,
              data: [leadId],
            }).unwrap();
            break;
        }

        showNotification(ACTION_TEXTS[actionId].success, SUCCESS);
        setConfirmationActionId(null);
        if (shouldRefetchLeadCard) {
          refetchLeadCard();
        }
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        setPendingActionId(null);
      }
    },
    [
      ActiveBranchId,
      applyLeadUpdate,
      leadCardData?.LeadId,
      markLeadsAsAnswered,
      markLeadsAsSpam,
      pendingActionId,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
      takeLead,
    ]
  );

  const confirmAction = useCallback(() => {
    if (confirmationActionId === null) return;

    void runAction(confirmationActionId);
  }, [confirmationActionId, runAction]);

  const actions = useMemo<LeadCardActionButtonItemData[]>(() => {
    if (!leadCardData) return [];

    const lead = leadCardData.Lead;
    if (lead.IsNoCommunication) return [];

    const isAnyActionPending = pendingActionId !== null;
    const getActionState = (
      actionId: LeadCardFooterActionId,
      disabled = false
    ) => ({
      disabled: disabled || isAnyActionPending,
      isLoading: pendingActionId === actionId,
      onClick: () => {
        if (actionId === ACCEPT_DIALOG_ACTION) {
          openConfirmation(actionId);
          return;
        }

        void runAction(actionId);
      },
    });

    if (lead.SpamTime) {
      if (!canManageSpam) return [];

      return [
        {
          id: RETURN_FROM_SPAM_ACTION,
          label: 'Не спам',
          priority: true,
          ...getActionState(RETURN_FROM_SPAM_ACTION),
        },
      ];
    }

    const result: LeadCardActionButtonItemData[] = [];

    if (lead.UserId !== UserId) {
      result.push({
        id: ACCEPT_DIALOG_ACTION,
        label: 'Принять диалог',
        priority: true,
        ...getActionState(ACCEPT_DIALOG_ACTION, Boolean(lead.UserId)),
      });
    }

    if (lead.IsNoAnswer) {
      result.push({
        id: DISABLE_ANSWERS_ACTION,
        label: 'Не требует ответа',
        ...getActionState(DISABLE_ANSWERS_ACTION),
      });
    }

    return result;
  }, [
    UserId,
    canManageSpam,
    leadCardData,
    openConfirmation,
    pendingActionId,
    runAction,
  ]);

  // Задачи приходят из ClientView.ClientCrmTasks — они принадлежат клиенту, а
  // не лиду, и одинаковы во всех его лидах. Спам-статус лида их больше не
  // скрывает (issue/11127): иначе в карточке спам-лида задачу создать можно, а
  // уже созданные не видны. Единственное условие — право на модуль задач.
  const tasks = useMemo<TaskPreviewItemData[]>(() => {
    if (!leadCardData || !canViewTasks) return [];

    return getTasks(leadCardData, openTask);
  }, [canViewTasks, leadCardData, openTask]);

  const confirmationText =
    confirmationActionId !== null ? ACTION_TEXTS[confirmationActionId] : null;

  const confirmation = useMemo<LeadCardFooterConfirmationState>(
    () => ({
      isOpen: confirmationText !== null,
      title: 'Подтверждение операции',
      text: confirmationText
        ? `Вы уверены, что хотите ${confirmationText.full}?`
        : '',
      okText: confirmationText?.short ?? '',
      cancelText: CANCEL,
      isDanger: false,
      isLoading: pendingActionId !== null,
      onCancel: closeConfirmation,
      onConfirm: confirmAction,
    }),
    [closeConfirmation, confirmAction, confirmationText, pendingActionId]
  );

  const answerTimer = useMemo<LeadCardAnswerTimerState>(
    () => ({
      isVisible: shouldShowAnswerTimer,
      text: shouldShowAnswerTimer
        ? `Осталось ${countdownLabel} до открепления диалога. Необходимо ответить.`
        : '',
    }),
    [countdownLabel, shouldShowAnswerTimer]
  );

  return useMemo(
    () => ({
      footerActions: actions,
      footerTasks: tasks,
      footerConfirmation: confirmation,
      answerTimer,
    }),
    [actions, answerTimer, confirmation, tasks]
  );
};
